import { firestore } from '../firebase/config';
import { collection, getDocs, getDoc, doc, addDoc, updateDoc, deleteDoc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';

// Récupérer tous les itinéraires
export const getAllRoutes = async (filters = {}) => {
  try {
    let routesQuery = collection(firestore, 'routes');
    
    // Appliquer les filtres
    const constraints = [];
    
    if (filters.origin) {
      constraints.push(where('origin', '==', filters.origin));
    }
    
    if (filters.destination) {
      constraints.push(where('destination', '==', filters.destination));
    }
    
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }
    
    // Ajouter le tri
    constraints.push(orderBy('createdAt', 'desc'));
    
    if (constraints.length > 0) {
      routesQuery = query(routesQuery, ...constraints);
    }
    
    const snapshot = await getDocs(routesQuery);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching routes:', error);
    throw new Error('Failed to fetch routes');
  }
};

// Récupérer un itinéraire par ID
export const getRouteById = async (routeId) => {
  try {
    const routeDoc = await getDoc(doc(firestore, 'routes', routeId));
    
    if (!routeDoc.exists()) {
      throw new Error('Route not found');
    }
    
    return {
      id: routeDoc.id,
      ...routeDoc.data()
    };
  } catch (error) {
    console.error('Error fetching route:', error);
    throw new Error('Failed to fetch route details');
  }
};

// Créer un nouvel itinéraire
export const createRoute = async (routeData) => {
  try {
    // Valider les données
    if (!routeData.origin || !routeData.destination) {
      throw new Error('Origin and destination are required');
    }
    
    if (!routeData.distance || routeData.distance <= 0) {
      throw new Error('Valid distance is required');
    }
    
    if (!routeData.estimatedDuration || routeData.estimatedDuration <= 0) {
      throw new Error('Valid estimated duration is required');
    }
    
    // Vérifier qu'un itinéraire similaire n'existe pas déjà
    const existingRoutes = await getDocs(
      query(
        collection(firestore, 'routes'),
        where('origin', '==', routeData.origin),
        where('destination', '==', routeData.destination),
        where('status', '==', 'active')
      )
    );
    
    if (!existingRoutes.empty) {
      throw new Error('An active route with the same origin and destination already exists');
    }
    
    // Préparer les données
    const newRoute = {
      origin: routeData.origin,
      destination: routeData.destination,
      distance: parseFloat(routeData.distance),
      estimatedDuration: parseInt(routeData.estimatedDuration),
      stops: routeData.stops || [],
      description: routeData.description || '',
      status: routeData.status || 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(firestore, 'routes'), newRoute);
    
    return {
      id: docRef.id,
      ...newRoute
    };
  } catch (error) {
    console.error('Error creating route:', error);
    throw error;
  }
};

// Mettre à jour un itinéraire
export const updateRoute = async (routeId, routeData) => {
  try {
    // Vérifier que l'itinéraire existe
    const routeDoc = await getDoc(doc(firestore, 'routes', routeId));
    
    if (!routeDoc.exists()) {
      throw new Error('Route not found');
    }
    
    // Valider les données si elles sont fournies
    if (routeData.distance !== undefined && routeData.distance <= 0) {
      throw new Error('Valid distance is required');
    }
    
    if (routeData.estimatedDuration !== undefined && routeData.estimatedDuration <= 0) {
      throw new Error('Valid estimated duration is required');
    }
    
    // Préparer les données de mise à jour
    const updateData = {
      ...routeData,
      updatedAt: serverTimestamp()
    };
    
    // Supprimer les champs non modifiables
    delete updateData.id;
    delete updateData.createdAt;
    
    await updateDoc(doc(firestore, 'routes', routeId), updateData);
    
    // Récupérer l'itinéraire mis à jour
    const updatedDoc = await getDoc(doc(firestore, 'routes', routeId));
    
    return {
      id: updatedDoc.id,
      ...updatedDoc.data()
    };
  } catch (error) {
    console.error('Error updating route:', error);
    throw error;
  }
};

// Supprimer un itinéraire
export const deleteRoute = async (routeId) => {
  try {
    // Vérifier que l'itinéraire existe
    const routeDoc = await getDoc(doc(firestore, 'routes', routeId));
    
    if (!routeDoc.exists()) {
      throw new Error('Route not found');
    }
    
    // Vérifier qu'aucun voyage actif n'utilise cet itinéraire
    const activeTrips = await getDocs(
      query(
        collection(firestore, 'trips'),
        where('routeId', '==', routeId),
        where('status', 'in', ['scheduled', 'in_progress'])
      )
    );
    
    if (!activeTrips.empty) {
      throw new Error('Cannot delete route with active trips. Please cancel or complete all trips first.');
    }
    
    // Supprimer l'itinéraire
    await deleteDoc(doc(firestore, 'routes', routeId));
    
    return { success: true, message: 'Route deleted successfully' };
  } catch (error) {
    console.error('Error deleting route:', error);
    throw error;
  }
};

// Obtenir les statistiques des itinéraires
export const getRouteStats = async () => {
  try {
    const routesSnapshot = await getDocs(collection(firestore, 'routes'));
    
    const stats = {
      total: routesSnapshot.size,
      active: 0,
      inactive: 0,
      totalDistance: 0,
      averageDistance: 0
    };
    
    routesSnapshot.docs.forEach(doc => {
      const data = doc.data();
      
      if (data.status === 'active') {
        stats.active++;
      } else {
        stats.inactive++;
      }
      
      if (data.distance) {
        stats.totalDistance += data.distance;
      }
    });
    
    if (stats.total > 0) {
      stats.averageDistance = stats.totalDistance / stats.total;
    }
    
    return stats;
  } catch (error) {
    console.error('Error fetching route stats:', error);
    throw new Error('Failed to fetch route statistics');
  }
};

// Rechercher des itinéraires
export const searchRoutes = async (searchTerm) => {
  try {
    // Firestore ne supporte pas la recherche full-text nativement
    // On récupère tous les itinéraires et on filtre côté client
    const allRoutes = await getAllRoutes();
    
    const searchLower = searchTerm.toLowerCase();
    
    return allRoutes.filter(route => 
      route.origin?.toLowerCase().includes(searchLower) ||
      route.destination?.toLowerCase().includes(searchLower) ||
      route.description?.toLowerCase().includes(searchLower)
    );
  } catch (error) {
    console.error('Error searching routes:', error);
    throw new Error('Failed to search routes');
  }
};

// Obtenir les villes disponibles (origine et destination)
export const getAvailableCities = async () => {
  try {
    const routesSnapshot = await getDocs(collection(firestore, 'routes'));
    
    const cities = new Set();
    
    routesSnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.origin) cities.add(data.origin);
      if (data.destination) cities.add(data.destination);
    });
    
    return Array.from(cities).sort();
  } catch (error) {
    console.error('Error fetching available cities:', error);
    throw new Error('Failed to fetch available cities');
  }
};

const routeService = {
  getAllRoutes,
  getRouteById,
  createRoute,
  updateRoute,
  deleteRoute,
  getRouteStats,
  searchRoutes,
  getAvailableCities
};

export default routeService;
